import {
	Backdrop,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Container,
	Divider,
	IconButton,
	TextField,
	Typography,
	useMediaQuery,
} from '@mui/material'
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import { useNavigate } from 'react-router-dom'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined'
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import axios from 'axios'
import { DOMAIN, api } from '../../utils/api'
import { handleError } from '../../utils/error'
import { currentUserActions } from '../../redux/store'
import { useDispatch, useSelector } from 'react-redux'
import { getFromStorage } from '../../utils/storage'
import PickDateWithoutShiftGrow from '../../components/PickDateWithoutShiftGrow'
import { borderFormat, mainGreen, mainGrey, mainRed } from '../../utils/color'

export default function StockManagement() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const mediaMd = useMediaQuery('(min-width:768px)')
	const token = getFromStorage('current-user')?.accessToken
	const { currentUser } = useSelector((state) => state.currentUser)
	const [showImportProposal, setShowImportProposal] = useState(false)
	const [stores, setStores] = useState([])
	const [storeIndex, setStoreIndex] = useState('')
	const [inventory, setInventory] = useState('')
	const [currentProposal, setCurrentProposal] = useState()
	const [currentStore, setCurrentStore] = useState()
	const [preview, setPreview] = useState([])
	const [currentImage, setCurrentImage] = useState(null)
	const [isViewerOpen, setIsViewerOpen] = useState(false)
	const [fileUpload, setFileUpload] = useState([])
	const [requests, setRequests] = useState([])
	const [loadingConfirm, setLoadingConfirm] = useState(false)
	const [products, setProducts] = useState([])
	const [importData, setImportData] = useState({
		values: {
			quantity: '',
			note: '',
		},
		errors: {
			quantity: '',
		},
	})

	const [exportData, setExportData] = useState({
		values: {
			quantity: '',
			unitPrice: '',
			note: '',
		},
		errors: {
			quantity: '',
		},
	})

	const currentDate = new Date()
	const [firstTimePick, setFirstTimePick] = useState({
		date: currentDate.getDate()?.toString(),
		month: (currentDate.getMonth() + 1)?.toString(),
		year: currentDate.getFullYear()?.toString(),
	})

	const [secondTimePick, setSecondTimePick] = useState({
		date: currentDate.getDate()?.toString(),
		month: (currentDate.getMonth() + 1)?.toString(),
		year: currentDate.getFullYear()?.toString(),
	})

	const setFirstTime = (time) => {
		setFirstTimePick(time)
	}

	const setSecondTime = (time) => {
		setSecondTimePick(time)
	}

	const openImageViewer = (photo) => {
		setCurrentImage(photo)
		setIsViewerOpen(true)
	}

	const closeImageViewer = () => {
		setCurrentImage(null)
		setIsViewerOpen(false)
	}

	const onFileUploadChange = async (e) => {
		setFileUpload(e.target.files)
	}

	useEffect(() => {
		if (!fileUpload) {
			setPreview(undefined)
			return
		}
		let preview = []
		for (let i = 0; i < fileUpload.length; i++) {
			preview.push(URL.createObjectURL(fileUpload[i]))
		}
		setPreview(preview)
	}, [fileUpload])

	const getStores = useCallback(
		async function () {
			try {
				const response = await axios({
					url: api.GET_STORES_ADMIN,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
					withCredentials: true,
				})
				setStores(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token],
	)

	useEffect(() => {
		getStores()
	}, [getStores])

	useEffect(() => {
		setStoreIndex(stores[0]?._id)
	}, [stores])

	const getInventory = useCallback(
		async function () {
			try {
				const response = await axios({
					url: api.GET_INVENTORY,
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				setInventory(response.data)
			} catch (err) {
				handleError(err)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[dispatch, navigate, token],
	)

	useEffect(() => {
		getInventory()
	}, [getInventory])

	const getRequests = useCallback(
		async function () {
			try {
				const response = await axios({
					url: api.GET_REQUESTS,
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				setRequests(response.data)
			} catch (err) {
				console.log(err.response.data.message)
			}
		},
		[token],
	)

	useEffect(() => {
		getRequests()
	}, [getRequests])

	const getProducts = async function () {
		try {
			const response = await axios({
				url: `${DOMAIN}/admin/stock/history-by-date/${storeIndex}`,
				method: 'POST',
				data: {
					firstTimePick,
					secondTimePick,
				},
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			setProducts(response.data)
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const handleChange = (e) => {
		let { name, value } = e.target
		let newValue = { ...importData.values, [name]: value }
		let newError = { ...importData.errors }

		if (name === 'quantity') {
			if (Number(value) <= 0 && value.trim() !== '') {
				newError[name] = '* Khối lượng nhập phải lớn hơn 0!'
			} else {
				newError[name] = ''
			}
		}

		setImportData({ values: newValue, errors: newError })
	}

	const handleChangeExport = (e) => {
		let { name, value } = e.target
		let newValue = { ...exportData.values, [name]: value }
		let newError = { ...exportData.errors }

		if (name === 'quantity') {
			if (Number(value) <= 0 && value.trim() !== '') {
				newError[name] = '* Khối lượng đề xuất phải lớn hơn 0!'
			} else {
				newError[name] = ''
			}
		}

		setExportData({ values: newValue, errors: newError })
	}

	const handleChangeUnitPrice = (e) => {
		let { name, value } = e.target
		const removeChar = value.replace(/[^0-9.]/g, '')
		const removeDot = removeChar.replace(/\./g, '')
		const formatedNumber = removeDot.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		let newValue = {
			...exportData.values,
			[name]: formatedNumber,
		}
		let newError = { ...exportData.errors }

		setExportData({ values: newValue, errors: newError })
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		let quantity = importData.values.quantity
		const note = importData.values.note
		if (quantity.trim() === '') {
			alert('Khối lượng không được để trống')
			return
		}
		if (importData.errors.quantity.trim() !== '') {
			return
		}
		try {
			const response = await axios({
				url: api.IMPORT_STOCK,
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: { quantity, note },
			})
			if (response.data.message) {
				alert(response.data.message)
			}
			await getInventory()
			setImportData({
				values: { quantity: '', note: '' },
				errors: { quantity: '' },
			})
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const approveRequest = async (id, e) => {
		e.preventDefault()
		const quantity = exportData.values.quantity
		const note = exportData.values.note
		const unitPrice = Number(exportData.values.unitPrice.replace(/\D/g, ''))
		if (quantity.trim() === '') {
			alert('Khối lượng không được để trống')
			return
		}
		if (exportData.errors.quantity.trim() !== '') {
			return
		}
		try {
			setLoadingConfirm(true)
			const formData = new FormData()
			formData.append('quantity', quantity)
			formData.append('id', id)
			formData.append('note', note)
			formData.append('unitPrice', unitPrice)
			for (let i = 0; i < fileUpload.length; i++) {
				formData.append('image', fileUpload[i])
			}
			const response = await axios({
				url: api.APPROVE_REQUEST,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
				data: formData,
			})
			if (response.data.message) {
				alert(response.data.message)
			}
			setLoadingConfirm(false)
			setExportData({
				values: { quantity: '', note: '', unitPrice: '' },
				errors: { quantity: '' },
			})
			setShowImportProposal(false)
			await getRequests()
			await getInventory()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const confirmRequest = async (id) => {
		try {
			await axios({
				url: api.CONFIRM_REQUEST,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: { id },
			})
			await getRequests()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const deleteRequest = async (id) => {
		try {
			await axios({
				url: api.DELETE_REQUEST,
				method: 'DELETE',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: { id },
			})
			await getRequests()
			setShowImportProposal(false)
			setCurrentProposal('')
			setCurrentStore('')
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const refresh = function () {
		getInventory()
		getRequests()
	}

	return (
		<UserLayout>
			<Box
				sx={{
					maxWidth: 992,
					margin: '0 auto',
				}}>
				<Card
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '10px',
					}}>
					<Backdrop
						sx={{
							color: '#fff',
							zIndex: 999,
							left: mediaMd ? 300 : 0,
						}}
						open={isViewerOpen}
						onClick={closeImageViewer}>
						<img
							loading='lazy'
							src={currentImage}
							alt='attach'
							style={{
								maxHeight: '70%',
								maxWidth: '90%',
							}}
							onClick={(e) => {
								e.stopPropagation()
							}}
						/>
					</Backdrop>
					<Button
						sx={{
							textTransform: 'none',
							padding: '0 5px',
							color: mainGreen,
							borderColor: mainGreen,
							'&:hover': {
								borderColor: mainGreen,
							},
						}}
						variant='outlined'
						size='small'
						onClick={refresh}>
						<AutorenewOutlinedIcon sx={{ fontSize: '16px', marginRight: '3px' }} />
						Cập nhật
					</Button>
					<h3 style={{ margin: 0 }}>TỒN KHO</h3>
					<Chip sx={{ color: '#fff', background: mainGreen }} label={`${Number(inventory).toFixed(1)} KG`} />
				</Card>
				<Card
					sx={{
						mt: 2,
						mb: 2,
						padding: '10px',
					}}>
					<Box>
						<Button
							variant='outlined'
							size='small'
							onClick={() => {
								navigate('/admin/stock/history')
							}}
							sx={{
								mr: 1,
								textTransform: 'none',
								color: mainRed,
								borderColor: mainRed,
								'&:hover': {
									borderColor: mainRed,
								},
							}}>
							Biến động
						</Button>
						{currentUser.permission?.includes('seeStockForecast') && (
							<Button
								variant='outlined'
								size='small'
								onClick={() => {
									navigate('/admin/stock/forecast')
								}}
								sx={{
									mr: 1,
									textTransform: 'none',
									color: mainGreen,
									borderColor: mainGreen,
									'&:hover': {
										borderColor: mainGreen,
									},
								}}>
								Dự báo
							</Button>
						)}

						<Button
							variant='outlined'
							size='small'
							onClick={() => {
								navigate('/admin/stock/params')
							}}
							sx={{
								textTransform: 'none',
								color: mainRed,
								borderColor: mainRed,
								'&:hover': {
									borderColor: mainRed,
								},
							}}>
							Thông số
						</Button>
					</Box>
					<Typography
						sx={{
							textAlign: 'center',
							fontSize: '18px',
							fontWeight: 'bold',
							mt: 2,
							mb: 1,
						}}>
						Nhập nem sống
					</Typography>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
						{importData.errors.quantity === '' ? (
							<TextField
								fullWidth
								type='number'
								value={importData.values.quantity}
								sx={{
									background: '#fff',
									boxShadow: 'inset 0px 1px 8px rgba(0, 0, 0, 0.1)',
								}}
								id='quantity'
								label='Khối lượng nhập (kg)'
								name='quantity'
								autoFocus
								autoComplete='quantity'
								onChange={handleChange}
							/>
						) : (
							<TextField
								fullWidth
								type='number'
								value={importData.values.quantity}
								sx={{
									background: '#fff',
									boxShadow: 'inset 0px 1px 8px rgba(0, 0, 0, 0.1)',
								}}
								error
								id='quantity'
								label='kg'
								name='quantity'
								autoComplete='quantity'
								onChange={handleChange}
							/>
						)}
					</Box>
					{importData.errors.quantity && (
						<Typography sx={{ color: '#ff0000', pt: 1 }}>{importData.errors.quantity}</Typography>
					)}
					<TextField
						value={importData.values.note}
						sx={{
							background: '#fff',
							mt: 2,
							boxShadow: 'inset 0px 1px 8px rgba(0, 0, 0, 0.1)',
						}}
						type='text'
						fullWidth
						name='note'
						label='Ghi chú'
						multiline
						autoComplete='note'
						onChange={handleChange}
					/>
					<Button
						sx={{
							mt: 2,
							background: mainGreen,
							'&:hover': { background: mainGreen },
						}}
						variant='contained'
						fullWidth
						onClick={(e) => {
							handleSubmit(e)
						}}>
						NHẬP KHO
					</Button>
					<Typography
						sx={{
							textAlign: 'center',
							fontSize: '18px',
							fontWeight: 'bold',
							mt: 2,
							mb: 1,
						}}>
						Yêu cầu xuất hàng
					</Typography>
					{requests.length === 0 && (
						<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
					)}
					{requests.length > 0 &&
						requests?.map((req, index) => {
							return (
								<Fragment key={index}>
									<Box display='flex' justifyContent='space-between' alignItems='flex-end'>
										<Box>
											{req.request.status === 'approved' && (
												<p
													style={{
														display: 'inline-flex',
														fontSize: '12px',
														color: '#fff',
														background: mainGreen,
														padding: '1px 5px',
														borderRadius: '5px',
														marginBottom: '2px',
														alignItems: 'flex-start',
													}}>
													<LocalShippingOutlinedIcon sx={{ fontSize: '18px', marginRight: '5px' }} />
													Đang giao hàng
												</p>
											)}
											{req.request.status === 'confirm' && (
												<p
													style={{
														display: 'inline-flex',
														fontSize: '12px',
														color: '#fff',
														background: mainGreen,
														padding: '1px 5px',
														borderRadius: '5px',
														marginBottom: '2px',
														alignItems: 'flex-start',
													}}>
													<HourglassTopOutlinedIcon sx={{ fontSize: '16px', marginRight: '5px' }} />
													Chờ cửa hàng xác nhận
												</p>
											)}
											<Typography
												sx={{
													display: 'flex',
													alignItems: 'center',
													mt: req.request.status === 'pending' ? 1 : 0,
												}}>
												<span style={{ marginRight: '5px', fontWeight: 'bold' }}>{req.request.store?.name}</span>{' '}
												{req.quantity && <b>: {req.quantity}kg</b>}
											</Typography>
											<Typography
												sx={{
													fontSize: '12px',
													color: mainGreen,
													fontWeight: 'bold',
												}}>
												{req.request.user?.name} tạo đề xuất - {req.request.timeStamp}
											</Typography>
											<Typography
												sx={{
													fontStyle: 'italic',
													fontSize: '14px',
												}}>
												{req.note && <span>{req.note}</span>}
											</Typography>
											{req?.deliveryReceiptPicture?.map((image, index) => {
												return (
													<img
														loading='lazy'
														key={index}
														src={image}
														onClick={(e) => {
															openImageViewer(image)
														}}
														width='30'
														height='30'
														style={{
															marginRight: '8px',
															marginTop: '2px',
															cursor: 'pointer',
														}}
														alt=''
													/>
												)
											})}
										</Box>

										{req.request.status === 'pending' && (
											<Box>
												<Button
													size='small'
													variant='contained'
													sx={{
														textTransform: 'none',
														mr: 1,
														minWidth: 0,
														padding: '0 5px',
														background: mainGreen,
														'&:hover': { background: mainGreen },
													}}
													onClick={() => {
														setCurrentProposal(req._id)
														setCurrentStore(req.request.store?.name)
														setShowImportProposal(true)
													}}>
													Duyệt
												</Button>
												<Button
													size='small'
													variant='contained'
													sx={{
														textTransform: 'none',
														padding: '0 5px',
														minWidth: 0,
														background: mainRed,
														'&:hover': { background: mainRed },
													}}
													onClick={() => {
														deleteRequest(req._id)
													}}>
													Hủy
												</Button>
											</Box>
										)}
										{req.request.status === 'approved' && (
											<Box>
												<Button
													size='small'
													variant='contained'
													sx={{
														textTransform: 'none',
														mr: 1,
														minWidth: 0,
														padding: '0 5px',
														background: mainRed,
														'&:hover': { background: mainRed },
													}}
													onClick={() => {
														setCurrentProposal(req._id)
														setCurrentStore(req.request.store?.name)
														setExportData({
															values: {
																quantity: req.quantity,
																unitPrice: req.unitPrice,
																note: req.note,
															},
															errors: {
																quantity: '',
															},
														})

														setShowImportProposal(true)
													}}>
													Sửa
												</Button>
												<Button
													size='small'
													variant='contained'
													sx={{
														textTransform: 'none',
														mr: 1,
														minWidth: 0,
														padding: '0 5px',
														background: mainGreen,
														'&:hover': { background: mainGreen },
													}}
													onClick={() => {
														confirmRequest(req._id)
													}}>
													Xác nhận
												</Button>
											</Box>
										)}
									</Box>
									<Divider sx={{ mt: 1 }} />
								</Fragment>
							)
						})}
				</Card>
				<Card
					sx={{
						padding: '10px',
					}}>
					<Typography
						sx={{
							textAlign: 'center',
							fontSize: '18px',
							fontWeight: 'bold',
							mt: 1,
							mb: 1,
						}}>
						Thống kê giá bán chi nhánh
					</Typography>
					<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
						{stores?.map((store, index) => {
							return (
								<Chip
									key={index}
									label={store.name}
									sx={{
										cursor: 'pointer',
										mr: 2,
										background: storeIndex === store._id ? mainGreen : undefined,
										color: storeIndex === store._id ? '#fff' : '#000',
										'&:hover': {
											background: storeIndex === store._id ? mainGreen : undefined,
											color: storeIndex === store._id ? '#fff' : '#000',
										},
									}}
									onClick={() => {
										setStoreIndex(store._id)
									}}
								/>
							)
						})}
					</Box>
					<Box display='flex' justifyContent='center' alignItems='flex-end'>
						<Typography sx={{ mr: 2, pb: 1 }}>Từ</Typography>
						<PickDateWithoutShiftGrow setTime={setFirstTime} time={firstTimePick} />
					</Box>
					<Box display='flex' justifyContent='center' alignItems='flex-end'>
						<Typography sx={{ mr: 2, pb: 1 }}>đến</Typography>
						<PickDateWithoutShiftGrow setTime={setSecondTime} time={secondTimePick} />
					</Box>
					<Box display='flex' justifyContent='center'>
						<Button
							sx={{
								textTransform: 'none',
								padding: '0 10px',
								mt: 1,
								mb: 2,
								background: mainGreen,
								'&:hover': { background: mainGreen },
							}}
							variant='contained'
							size='small'
							onClick={() => {
								getProducts()
							}}>
							Xác nhận
						</Button>
					</Box>
					<Backdrop
						sx={{
							color: '#fff',
							zIndex: 999,
							left: mediaMd ? 300 : 0,
						}}
						open={isViewerOpen}
						onClick={closeImageViewer}>
						<img
							loading='lazy'
							src={currentImage}
							alt='attach'
							style={{
								maxHeight: '70%',
								maxWidth: '90%',
							}}
							onClick={(e) => {
								e.stopPropagation()
							}}
						/>
					</Backdrop>
					{products.length === 0 && (
						<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
					)}
					{products?.map((prod, index) => {
						return (
							<Fragment key={index}>
								{prod.action === 'export' && prod.request.status === 'complete' && (
									<>
										<Box display='flex' alignItems='center'>
											<ArrowDropUpIcon sx={{ fontSize: '20px', color: mainGreen }} />
											<Typography>
												<span style={{ color: mainGreen, fontWeight: 'bold' }}>(+{prod.request?.quantity})</span> nhập
												từ <b>{prod.user?.name}</b>.{' '}
												{(prod?.unitPrice || prod?.unitPrice === 0) && (
													<span>
														Đơn giá{' '}
														<span style={{ color: mainGreen, fontWeight: 'bold' }}>
															{prod?.unitPrice.toLocaleString()}
														</span>
														đ.
													</span>
												)}{' '}
												{(prod?.unitPrice || prod?.unitPrice === 0) && (
													<span>
														Doanh thu{' '}
														<span style={{ color: mainRed, fontWeight: 'bold' }}>
															{(Math.round(prod.quantity * 0.98) * prod?.unitPrice).toLocaleString()}
														</span>
														đ.
													</span>
												)}{' '}
												{prod.note && <span style={{ fontStyle: 'italic' }}>({prod.note}).</span>}{' '}
												<span>
													Tồn: <b>{Number(prod.balanceStore)?.toFixed(1)}</b>
													kg
												</span>
											</Typography>
										</Box>
										<Box display='flex' justifyContent='flex-end' alignItems='flex-end' sx={{ mb: 1 }}>
											{prod.request.confirmPicture && (
												<img
													loading='lazy'
													src={prod.request.confirmPicture}
													onClick={(e) => {
														openImageViewer(prod.request.confirmPicture)
													}}
													width='30'
													height='30'
													style={{
														marginRight: '8px',
														marginTop: '2px',
														cursor: 'pointer',
													}}
													alt=''
												/>
											)}
											{prod?.deliveryReceiptPicture?.map((image, index) => {
												return (
													<img
														loading='lazy'
														key={index}
														src={image}
														onClick={(e) => {
															openImageViewer(image)
														}}
														width='30'
														height='30'
														style={{
															marginRight: '8px',
															marginTop: '2px',
															cursor: 'pointer',
														}}
														alt=''
													/>
												)
											})}
											{prod.request?.confirmMultiPicture?.map((image, index) => {
												return (
													<img
														loading='lazy'
														key={index}
														src={image}
														onClick={(e) => {
															openImageViewer(image)
														}}
														width='30'
														height='30'
														style={{
															marginRight: '8px',
															marginTop: '2px',
															cursor: 'pointer',
														}}
														alt=''
													/>
												)
											})}
											<Typography
												style={{
													fontStyle: 'italic',
													fontSize: '12px',
													textAlign: 'right',
													marginTop: '3px',
												}}>
												- {prod.timeStamp} -
											</Typography>{' '}
										</Box>

										<Divider sx={{ mb: 1 }} />
									</>
								)}
							</Fragment>
						)
					})}
				</Card>
			</Box>
			<Box
				position='fixed'
				sx={{
					bottom: 0,
					left: mediaMd ? 300 : 0,
					right: 0,
					zIndex: 99,
				}}>
				{showImportProposal && currentUser.permission?.includes('manageStock') && (
					<Container
						sx={{
							background: mainGrey,
							borderTop: borderFormat,
							borderLeft: borderFormat,
							borderRight: borderFormat,
							maxWidth: '600px',
							pt: 2,
							pb: 2,
						}}>
						<Typography
							sx={{
								textAlign: 'center',
								fontWeight: 'bold',
								mb: 1,
							}}>
							Đề xuất nhập từ {currentStore}
						</Typography>
						{loadingConfirm && (
							<Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
								<CircularProgress sx={{ color: mainGreen }} />
							</Box>
						)}
						{!loadingConfirm && (
							<>
								<Box
									sx={{
										mt: 1,
										mb: 2,
										display: 'flex',
										alignItems: 'flex-end',
									}}>
									{' '}
									<Typography sx={{ mr: 2 }}>Thêm hình:</Typography>
									{preview?.map((img, index) => {
										return (
											<img
												loading='lazy'
												key={index}
												style={{
													width: 50,
													marginRight: '8px',
												}}
												src={img}
												alt='preview'
											/>
										)
									})}
									<>
										<IconButton sx={{ padding: '0' }}>
											<label htmlFor='choose-photo-import' style={{ display: 'flex', cursor: 'pointer' }}>
												<AddPhotoAlternateRoundedIcon sx={{ color: mainGreen }} />
											</label>
										</IconButton>
										<input
											multiple
											accept='image/*'
											onChange={onFileUploadChange}
											id='choose-photo-import'
											type='file'
											style={{ display: 'none' }}
										/>
									</>
								</Box>
								{exportData.errors.quantity === '' ? (
									<TextField
										type='number'
										value={exportData.values.quantity}
										sx={{
											background: '#fff',
											width: '50%',
										}}
										id='quantity'
										label='Khối lượng xuất (kg)'
										name='quantity'
										autoFocus
										autoComplete='quantity'
										onChange={handleChangeExport}
									/>
								) : (
									<TextField
										type='number'
										value={exportData.values.quantity}
										sx={{
											background: '#fff',
											width: '50%',
										}}
										error
										id='quantity'
										label='kg'
										name='quantity'
										autoComplete='quantity'
										onChange={handleChangeExport}
									/>
								)}
								<TextField
									value={exportData.values.unitPrice}
									sx={{ background: '#fff', width: '50%' }}
									type='text'
									fullWidth
									name='unitPrice'
									label='Đơn giá (vnđ)'
									multiline
									autoComplete='unitPrice'
									onChange={handleChangeUnitPrice}
								/>
								{exportData.errors.quantity && (
									<Typography sx={{ color: '#ff0000', pt: 1 }}>{exportData.errors.quantity}</Typography>
								)}

								<TextField
									value={exportData.values.note}
									sx={{ background: '#fff', mt: 2 }}
									type='text'
									fullWidth
									name='note'
									label='Ghi chú'
									multiline
									autoComplete='note'
									onChange={handleChangeExport}
								/>
								<Box display='flex' justifyContent='space-between'>
									<Button
										sx={{
											mt: 2,
											width: '75%',
											background: mainGreen,
											'&:hover': { background: mainGreen },
										}}
										variant='contained'
										onClick={(e) => {
											approveRequest(currentProposal, e)
										}}>
										XUẤT KHO
									</Button>
									<Button
										sx={{
											mt: 2,
											width: '23%',
											background: mainRed,
											'&:hover': { background: mainRed },
										}}
										variant='contained'
										onClick={() => {
											setShowImportProposal(false)
										}}>
										HỦY
									</Button>
								</Box>
							</>
						)}
					</Container>
				)}
			</Box>
		</UserLayout>
	)
}
